import { useState, useEffect, useCallback, useMemo, useRef, Suspense } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Group, Panel, Separator, usePanelCallbackRef } from 'react-resizable-panels';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { motion } from 'motion/react';
import { NavBar, MobileBottomNav } from './NavBar';
import { CommandPalette } from './CommandPalette';
import { SessionJumpPalette } from './SessionJumpPalette';
import { HotkeysDialog } from './HotkeysDialog';
import { NavPanelContext } from './nav-panel-context';
import { PageCommandsProvider } from './PageCommandsProvider';
import {
  accountExtraFirstSegments,
  isAccountPath,
  isLabsPath,
  isTakeoverPath,
  labsExtraFirstSegments,
  rememberTakeoverReturnPath,
  shouldRememberReturnPath,
} from './TakeoverShell';
import { ROUTES } from '@/lib/routes';
import type { SessionJumpMode } from '@/lib/session-jump';
import { useSocketConnection, useSocketOrgReconnect } from '@/hooks/socket/useSocket';
import { useAuth } from '@/hooks/auth/useAuth';
import { useVersionCheck } from '@/hooks/ui/useVersionCheck';
import { useMaintenanceSync } from '@/hooks/config/useMaintenance';
import { MaintenanceBanner } from './MaintenanceBanner';
import { useIsDesktop } from '@reflex/ui/hooks/useIsDesktop';
import { useOnScreenKeyboardOpen } from '@/hooks/ui/useOnScreenKeyboardOpen';
import { useKeyboardViewportInset } from '@/hooks/ui/useKeyboardViewportInset';
import { useDocumentScrollLock } from '@/hooks/ui/useDocumentScrollLock';
import { useHotkey } from '@/hooks/ui/useHotkey';
import { useAppBadge } from '@/hooks/browser/useAppBadge';
import { useAgentNotifications } from '@/hooks/browser/useAgentNotifications';
import { useViewTransition } from '@/hooks/browser/useViewTransition';
import { usePlugins } from '@/hooks/config/usePlugins';
import { useOrgPluginInstallationSocket } from '@/hooks/plugins/useOrgPluginInstallationSocket';
import { pageFade } from '@/lib/motion';
import { getPluginComponent } from '@/lib/plugin-registry';
import { transitionKeyForPath } from './transitionKeyForPath';

const HelpPopoverComponent = getPluginComponent('@reflex/plugin-help/web/HelpPopover');

const NAV_COLLAPSED_WIDTH = 48;
// 256 gives the sessions dock its designed proportions: the org selector,
// the combined search bar, and the labeled doors all fit without
// truncation at the default width.
const NAV_DEFAULT_WIDTH = 256;
const NAV_MIN_WIDTH = 180;
const NAV_MAX_WIDTH_FALLBACK = 320;

function getNavMaxWidth(): number {
  return typeof window !== 'undefined'
    ? Math.max(NAV_MAX_WIDTH_FALLBACK, Math.round(window.innerWidth / 3))
    : NAV_MAX_WIDTH_FALLBACK;
}

function getInitialNavWidth(): number {
  try {
    const v = localStorage.getItem('nav-sidebar-width');
    return v ? Number(v) : NAV_DEFAULT_WIDTH;
  } catch {
    return NAV_DEFAULT_WIDTH;
  }
}

export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  // `null` while the jump palette is closed; the mode doubles as its open flag.
  const [sessionJumpMode, setSessionJumpMode] = useState<SessionJumpMode | null>(null);
  const [hotkeysOpen, setHotkeysOpen] = useState(false);
  const wsState = useSocketConnection();
  const { activeOrganizationId } = useAuth();
  useSocketOrgReconnect(activeOrganizationId);
  // Keep plugin manifests (nav / integrations sidebar) in sync with
  // install/uninstall even when the user is not on the plugins pages.
  useOrgPluginInstallationSocket(activeOrganizationId);
  useVersionCheck();
  useMaintenanceSync();
  useAppBadge();
  useAgentNotifications();
  const isDesktop = useIsDesktop();
  const keyboardOpen = useOnScreenKeyboardOpen();
  // Shrinks full-height (`h-app`) surfaces to the visible area when the mobile
  // keyboard opens so their content stays scrollable above it (iOS especially).
  useKeyboardViewportInset();
  // The shell scrolls internally, so the document must not scroll behind it —
  // on iOS that is what strands chat content above the keyboard.
  useDocumentScrollLock();

  const [navPanel, setNavPanel] = usePanelCallbackRef();
  const [initialWidth] = useState(getInitialNavWidth);
  const navWidthRef = useRef(initialWidth);
  const [navWidthPx, setNavWidthPx] = useState(initialWidth);
  const [borderHovered, setBorderHovered] = useState(false);
  const [navMaxWidth, setNavMaxWidth] = useState(getNavMaxWidth);

  useEffect(() => {
    const update = () => setNavMaxWidth(getNavMaxWidth());
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (!navPanel) return;
    if (collapsed) {
      navPanel.collapse();
    } else {
      navPanel.expand();
    }
  }, [collapsed, navPanel]);

  const openCommand = useCallback(() => setCommandOpen(true), []);
  // The two palettes are both modal dialogs, so opening the jump palette (by
  // shortcut or from the ⌘K Actions row) dismisses the command palette rather
  // than stacking on top of it.
  const setSessionJump = useCallback((mode: SessionJumpMode | null) => {
    if (mode !== null) setCommandOpen(false);
    setSessionJumpMode(mode);
  }, []);
  // Same rule for the shortcut listing: it replaces ⌘K rather than stacking on
  // it, whether it was opened from the palette's row or by its own chord.
  const setHotkeys = useCallback((next: boolean) => {
    if (next) setCommandOpen(false);
    setHotkeysOpen(next);
  }, []);
  const openHotkeys = useCallback(() => setHotkeys(true), [setHotkeys]);
  // Stable controls so page content (the agent stream's sidebar toggle when
  // the sessions dock is active) can collapse the nav panel.
  const navPanelControls = useMemo(() => ({ toggle: () => setCollapsed((value) => !value) }), []);
  const location = useLocation();
  const navigate = useNavigate();
  const { globalUiElements, navItems: pluginNavItems } = usePlugins();

  // Takeover surfaces (settings, inbox, automations, labs) bring their own
  // chrome — the app sidebar steps aside on those paths. Labs pages
  // contributed by plugins (e.g. `/town`) are classified from the same
  // manifests that register their routes. Account pages (`/profile`,
  // `/settings`, and plugin user-menu routes like `/tutorials`,
  // `/notifications`) are a takeover too, so the agent session list doesn't
  // persist as the sidebar on account surfaces.
  const labsExtraSegments = useMemo(() => labsExtraFirstSegments(pluginNavItems), [pluginNavItems]);
  const accountExtraSegments = useMemo(
    () => accountExtraFirstSegments(pluginNavItems),
    [pluginNavItems],
  );
  // Drives both the view transition and the page-fade remount keys below.
  // Pass account extras so Profile/Settings/plugin account pages share one
  // key and switching options does not remount the Account shell.
  const topSegment = transitionKeyForPath(location.pathname, accountExtraSegments);
  useViewTransition(topSegment);
  const inTakeover =
    isTakeoverPath(location.pathname) ||
    isLabsPath(location.pathname, labsExtraSegments) ||
    isAccountPath(location.pathname, accountExtraSegments);

  // Remember the last work location so every takeover's Back / `esc` restores it.
  useEffect(() => {
    if (shouldRememberReturnPath(location.pathname, labsExtraSegments, accountExtraSegments)) {
      rememberTakeoverReturnPath(location.pathname + location.search);
    }
  }, [location.pathname, location.search, labsExtraSegments, accountExtraSegments]);

  // `⌥⌘,` (or `Ctrl+Alt+,`) opens settings from anywhere, matching the
  // takeover's `esc` on the way out. Plain `⌘,` is every browser's own
  // Settings key equivalent, so it is kept only as a legacy chord for the
  // platforms that do pass it through (see `HOTKEYS`). Lands on personal
  // settings, the takeover's top level.
  useHotkey('openSettings', () => void navigate(ROUTES.settings));

  return (
    // PageCommandsProvider sits above both the routed page tree and the
    // palette so any mounted page can contribute "On this page" commands.
    <PageCommandsProvider>
      <NavPanelContext.Provider value={navPanelControls}>
        <div
          className="flex h-app flex-col bg-background text-foreground"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          {globalUiElements.length > 0 && (
            <Suspense fallback={null}>
              {globalUiElements.map(({ id, pluginName, component: Component }) => (
                <Component key={`${pluginName}:${id}`} />
              ))}
            </Suspense>
          )}
          {/* `app-dock-inset` reserves the strip a side-docked global panel
              (Chief) claims, so it sits beside the page instead of over it.
              Deliberately untransitioned: the panel is resized by dragging its
              edge, and easing `padding` would leave the page rubber-banding a
              frame behind the pointer for the whole gesture. */}
          <div className="app-dock-inset flex min-h-0 flex-1">
            {isDesktop && inTakeover ? (
              <main
                aria-label="Desktop content"
                className="flex h-full flex-1 flex-col overflow-hidden"
                style={{ viewTransitionName: 'main-content' }}
              >
                <MaintenanceBanner />
                <motion.div key={topSegment} className="flex min-h-0 flex-1 flex-col" {...pageFade}>
                  <Outlet />
                </motion.div>
              </main>
            ) : isDesktop ? (
              <div className="relative h-full min-w-0 flex-1">
                <Group orientation="horizontal" id="app-shell" className="h-full">
                  <Panel
                    id="nav"
                    panelRef={setNavPanel}
                    defaultSize={initialWidth}
                    minSize={NAV_MIN_WIDTH}
                    maxSize={navMaxWidth}
                    collapsible
                    collapsedSize={NAV_COLLAPSED_WIDTH}
                    groupResizeBehavior="preserve-pixel-size"
                    onResize={(size) => {
                      const nowCollapsed = size.inPixels <= NAV_COLLAPSED_WIDTH;
                      setCollapsed(nowCollapsed);
                      navWidthRef.current = size.inPixels;
                      setNavWidthPx(size.inPixels);
                      if (nowCollapsed) return;
                      try {
                        localStorage.setItem('nav-sidebar-width', String(size.inPixels));
                      } catch {
                        /* noop */
                      }
                    }}
                  >
                    <NavBar
                      collapsed={collapsed}
                      onToggle={() => setCollapsed(!collapsed)}
                      wsState={wsState}
                      onCommandOpen={openCommand}
                    />
                  </Panel>
                  <NavResizeHandle onHoverChange={setBorderHovered} />
                  <Panel id="main">
                    <main
                      aria-label="Desktop content"
                      className="flex h-full flex-1 flex-col overflow-hidden"
                      style={{ viewTransitionName: 'main-content' }}
                    >
                      <MaintenanceBanner />
                      <motion.div
                        key={topSegment}
                        className="flex min-h-0 flex-1 flex-col"
                        {...pageFade}
                      >
                        <Outlet />
                      </motion.div>
                    </main>
                  </Panel>
                </Group>
                <CollapseChevron
                  collapsed={collapsed}
                  onToggle={() => setCollapsed(!collapsed)}
                  navWidth={navWidthPx}
                  borderHovered={borderHovered}
                  onBorderHover={setBorderHovered}
                />
              </div>
            ) : (
              <>
                <main
                  aria-label="Mobile content"
                  className="flex flex-1 flex-col overflow-hidden"
                  style={{
                    // Reserves the fixed MobileBottomNav's exact height (a 3.5rem
                    // contract with its h-[calc(3.5rem+…)]). While the on-screen
                    // keyboard is up the bar slides away and the reservation drops
                    // with it, handing the space to the content + composer.
                    paddingBottom: keyboardOpen ? 0 : 'calc(3.5rem + env(safe-area-inset-bottom))',
                    viewTransitionName: 'main-content',
                  }}
                >
                  <MaintenanceBanner />
                  <motion.div
                    key={topSegment}
                    className="flex min-h-0 flex-1 flex-col"
                    {...pageFade}
                  >
                    <Outlet />
                  </motion.div>
                </main>
                <MobileBottomNav
                  wsState={wsState}
                  onCommandOpen={openCommand}
                  hidden={keyboardOpen}
                />
              </>
            )}
          </div>

          <CommandPalette
            open={commandOpen}
            onOpenChange={setCommandOpen}
            onJumpToSession={setSessionJump}
            onViewHotkeys={openHotkeys}
          />
          <SessionJumpPalette mode={sessionJumpMode} onModeChange={setSessionJump} />
          <HotkeysDialog open={hotkeysOpen} onOpenChange={setHotkeys} />
          {HelpPopoverComponent && (
            <Suspense fallback={null}>
              <HelpPopoverComponent />
            </Suspense>
          )}
        </div>
      </NavPanelContext.Provider>
    </PageCommandsProvider>
  );
}

function CollapseChevron({
  collapsed,
  onToggle,
  navWidth,
  borderHovered,
  onBorderHover,
}: {
  collapsed: boolean;
  onToggle: () => void;
  navWidth: number;
  borderHovered: boolean;
  onBorderHover: (hovered: boolean) => void;
}) {
  const Icon = collapsed ? ChevronRight : ChevronLeft;
  return (
    <button
      type="button"
      onClick={onToggle}
      onMouseEnter={() => onBorderHover(true)}
      onMouseLeave={() => onBorderHover(false)}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      data-testid="sidebar-collapse-affordance"
      className={cn(
        'absolute top-1/2 z-30 flex h-7 w-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md border border-border bg-background text-muted-foreground shadow-sm transition-[color,background-color,opacity] hover:bg-accent hover:text-foreground hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        collapsed ? 'opacity-100' : 'opacity-0',
        borderHovered && 'opacity-100',
      )}
      style={{ left: navWidth - 10 }}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

function NavResizeHandle({ onHoverChange }: { onHoverChange: (hovered: boolean) => void }) {
  return (
    <Separator
      className="group relative flex w-2 items-center justify-center bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      aria-label="Resize navigation"
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    >
      <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gradient-to-b from-border/10 via-border to-border/10 transition-colors group-hover:via-ring/50" />
    </Separator>
  );
}
